import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertTriangle, Clock, CheckCircle, XCircle } from 'lucide-react';
import { useSubscriptionLimits } from '@/hooks/useSubscriptionLimits';
import { Badge } from '@/components/ui/badge';

interface SubscriptionLimitAlertProps {
  showOnlyWhenBlocked?: boolean;
}

export function SubscriptionLimitAlert({ showOnlyWhenBlocked = false }: SubscriptionLimitAlertProps) {
  const { canCreate, currentCount, limitCount, planName, expiresAt, loading, error } = useSubscriptionLimits();

  if (loading) return null;
  if (error) return null;
  if (showOnlyWhenBlocked && canCreate) return null;

  const getAlertVariant = () => {
    if (!canCreate) return 'destructive';
    if (currentCount >= limitCount * 0.8) return 'default';
    return 'default';
  };

  const getIcon = () => {
    if (!canCreate) return <XCircle className="h-4 w-4" />;
    if (currentCount >= limitCount * 0.8) return <AlertTriangle className="h-4 w-4" />;
    return <CheckCircle className="h-4 w-4" />;
  };

  const getTitle = () => {
    if (!canCreate) {
      if (expiresAt && new Date(expiresAt) < new Date()) {
        return 'Assinatura Expirada';
      }
      return 'Limite de Contratações Atingido';
    }
    if (currentCount >= limitCount * 0.8) return 'Limite Próximo do Esgotamento';
    return 'Controle de Contratações';
  };

  const getDescription = () => {
    if (!canCreate) {
      if (expiresAt && new Date(expiresAt) < new Date()) {
        return `Sua assinatura do plano ${planName} expirou em ${new Date(expiresAt).toLocaleDateString('pt-BR')}. Renove sua assinatura para continuar criando contratações.`;
      }
      return `Você atingiu o limite de ${limitCount} contratações mensais do plano ${planName}. Aguarde o próximo período ou faça upgrade do seu plano.`;
    }
    
    return `Você usou ${currentCount} de ${limitCount} contratações disponíveis no plano ${planName} este mês.`;
  };

  return (
    <Alert variant={getAlertVariant()} className="mb-4">
      {getIcon()}
      <AlertTitle className="flex items-center gap-2">
        {getTitle()}
        <Badge variant="outline" className="text-xs">
          Plano {planName}
        </Badge>
      </AlertTitle>
      <AlertDescription className="mt-2">
        {getDescription()}
        
        <div className="mt-3 flex items-center gap-4 text-sm">
          <div className="flex items-center gap-1">
            <span className="font-medium">Usado:</span>
            <span className={currentCount >= limitCount ? 'text-destructive font-semibold' : ''}>
              {currentCount}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <span className="font-medium">Limite:</span>
            <span>{limitCount}</span>
          </div>
          {expiresAt && (
            <div className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              <span className="font-medium">Expira:</span>
              <span>{new Date(expiresAt).toLocaleDateString('pt-BR')}</span>
            </div>
          )}
        </div>
      </AlertDescription>
    </Alert>
  );
}